import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { scrollableTableWrapStyles, scrollableTableSizing } from '../styles/scrollableTable';
import { FiTruck, FiPlus, FiEdit, FiTrash2, FiX, FiRefreshCw, FiSearch } from 'react-icons/fi';
import { supabase } from '../../config/supabase';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
`;

const Button = styled.button`
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${props => props.variant === 'primary' ? '#5efba9' : 'white'};
  color: ${props => props.variant === 'primary' ? '#1f2937' : '#6b7280'};
  border: ${props => props.variant === 'secondary' ? '1px solid #e5e7eb' : 'none'};

  &:hover {
    background: ${props => props.variant === 'primary' ? '#4de89e' : '#f9fafb'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TableWrapper = styled.div`
  ${scrollableTableWrapStyles}
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const Table = styled.table`
  ${scrollableTableSizing}
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background: #f9fafb;
`;

const TableHeaderRow = styled.tr`
  border-bottom: 1px solid #e5e7eb;
`;

const TableHeaderCell = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid #f3f4f6;
  transition: background 0.2s;

  &:hover {
    background: #f9fafb;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  color: #1f2937;
`;

const ActionButtonsCell = styled.div`
  display: flex;
  gap: 8px;
`;

const StatusBadge = styled.span`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => props.active ? '#dcfce7' : '#fee2e2'};
  color: ${props => props.active ? '#166534' : '#dc2626'};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
`;

const EmptyText = styled.p`
  font-size: 16px;
  color: #9ca3af;
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 32px;
  position: relative;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  border-radius: 6px;

  &:hover {
    background: #f3f4f6;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`;

const Input = styled.input`
  padding: 10px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #5efba9;
  }
`;

const Select = styled.select`
  padding: 10px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #5efba9;
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  color: #374151;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 8px;
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid #e5e7eb;
  background: white;
  color: #6b7280;

  &:hover {
    background: #f9fafb;
  }
`;

const InfoBox = styled.div`
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  padding: 12px;
  border-radius: 8px;
  font-size: 13px;
  color: #1e40af;
  line-height: 1.5;
`;

const SearchContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 400px;
  margin-bottom: 16px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px 12px 44px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #5efba9;
    box-shadow: 0 0 0 3px rgba(94, 251, 169, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const SearchIcon = styled(FiSearch)`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  font-size: 18px;
  pointer-events: none;
`;

const ClearButton = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #9ca3af;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background: #f3f4f6;
    color: #6b7280;
  }
`;

export default function SuperAdminShippingRates() {
  const [countries, setCountries] = useState([]);
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRate, setEditingRate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    country_id: '',
    base_price: 0,
    base_weight_kg: 1.000,
    extra_per_kg: 0,
    currency: 'OMR',
    is_active: true
  });

  useEffect(() => {
    fetchCountries();
    fetchRates();
  }, []);

  const fetchCountries = async () => {
    try {
      const { data, error } = await supabase
        .from('shipping_countries')
        .select('*')
        .order('name', { ascending: true });

      if (error) throw error;
      setCountries(data || []);
    } catch (err) {
      console.error('Error fetching countries:', err);
    }
  };

  const fetchRates = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('shipping_rates')
        .select(`
          *,
          shipping_countries(id, code, name)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRates(data || []);
    } catch (err) {
      console.error('Error fetching rates:', err);
      alert('Failed to fetch rates: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (rate) => {
    setEditingRate(rate);
    setFormData({
      country_id: rate.country_id || '',
      base_price: rate.base_price || 0,
      base_weight_kg: rate.base_weight_kg || 1.000,
      extra_per_kg: rate.extra_per_kg || 0,
      currency: rate.currency || 'OMR',
      is_active: rate.is_active !== undefined ? rate.is_active : true
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (rateId) => {
    if (!window.confirm('Are you sure you want to delete this shipping rate?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('shipping_rates')
        .delete()
        .eq('id', rateId);

      if (error) throw error;
      
      alert('Shipping rate deleted successfully!');
      fetchRates();
    } catch (err) {
      console.error('Error deleting rate:', err);
      alert('Failed to delete rate: ' + err.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const rateData = {
        country_id: formData.country_id,
        base_price: parseFloat(formData.base_price) || 0,
        base_weight_kg: parseFloat(formData.base_weight_kg) || 1.000,
        extra_per_kg: parseFloat(formData.extra_per_kg) || 0,
        currency: formData.currency,
        is_active: formData.is_active
      };

      let error;
      if (editingRate) {
        const { error: updateError } = await supabase
          .from('shipping_rates')
          .update(rateData)
          .eq('id', editingRate.id);
        error = updateError;
      } else {
        const { error: insertError } = await supabase
          .from('shipping_rates')
          .insert([rateData]);
        error = insertError;
      }

      if (error) throw error;
      
      alert(editingRate ? 'Shipping rate updated successfully!' : 'Shipping rate added successfully!');
      setIsModalOpen(false);
      setEditingRate(null);
      setFormData({
        country_id: '',
        base_price: 0,
        base_weight_kg: 1.000,
        extra_per_kg: 0,
        currency: 'OMR',
        is_active: true
      });
      fetchRates();
    } catch (err) {
      console.error('Error saving rate:', err);
      alert('Failed to save rate: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingRate(null);
    setFormData({
      country_id: '',
      base_price: 0,
      base_weight_kg: 1.000,
      extra_per_kg: 0,
      currency: 'OMR',
      is_active: true
    });
  };

  // Filter rates by search term
  const filteredRates = useMemo(() => {
    if (!searchTerm.trim()) {
      return rates;
    }
    const term = searchTerm.toLowerCase();
    return rates.filter(rate => {
      const countryName = (rate.shipping_countries?.name || '').toLowerCase();
      const countryCode = (rate.shipping_countries?.code || '').toLowerCase();
      const currency = (rate.currency || '').toLowerCase();
      const basePrice = (rate.base_price || 0).toString();
      const baseWeight = (rate.base_weight_kg || 0).toString();
      const extraPerKg = (rate.extra_per_kg || 0).toString();
      const status = rate.is_active ? 'active' : 'inactive';
      
      return (
        countryName.includes(term) ||
        countryCode.includes(term) ||
        currency.includes(term) ||
        basePrice.includes(term) ||
        baseWeight.includes(term) ||
        extraPerKg.includes(term) ||
        status.includes(term)
      );
    });
  }, [rates, searchTerm]);

  return (
    <>
      <Container>
        <Header>
          <Title>
            <FiTruck /> Shipping Rates
          </Title>
          <HeaderActions>
            <Button variant="secondary" onClick={fetchRates}>
              <FiRefreshCw /> Refresh
            </Button>
            <Button variant="primary" onClick={() => setIsModalOpen(true)}>
              <FiPlus /> Add Rate
            </Button>
          </HeaderActions>
        </Header>

        <SearchContainer>
          <SearchIcon />
          <SearchInput
            type="text"
            placeholder="Search by country, code, currency, price, weight, or status..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <ClearButton onClick={() => setSearchTerm('')} title="Clear search">
              <FiX size={18} />
            </ClearButton>
          )}
        </SearchContainer>

        {loading ? (
          <EmptyState>Loading rates...</EmptyState>
        ) : rates.length === 0 ? (
          <TableWrapper>
            <EmptyState>
              <EmptyText>No shipping rates yet. Add your first rate.</EmptyText>
            </EmptyState>
          </TableWrapper>
        ) : (
          <TableWrapper>
            <Table>
              <TableHeader>
                <TableHeaderRow>
                  <TableHeaderCell>Country</TableHeaderCell>
                  <TableHeaderCell>Base Price</TableHeaderCell>
                  <TableHeaderCell>Base Weight</TableHeaderCell>
                  <TableHeaderCell>Extra/kg</TableHeaderCell>
                  <TableHeaderCell>Currency</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                  <TableHeaderCell>Created</TableHeaderCell>
                  <TableHeaderCell>Actions</TableHeaderCell>
                </TableHeaderRow>
              </TableHeader>
              <TableBody>
                {filteredRates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan="8" style={{ textAlign: 'center', padding: '40px' }}>
                      <EmptyText>{searchTerm ? 'No shipping rates found matching your search' : 'No shipping rates available'}</EmptyText>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRates.map((rate) => (
                  <TableRow key={rate.id}>
                    <TableCell>
                      <strong>{rate.shipping_countries?.name || 'Unknown'}</strong>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>
                        {rate.shipping_countries?.code}
                      </div>
                    </TableCell>
                    <TableCell>
                      <strong>{rate.currency} {parseFloat(rate.base_price).toFixed(3)}</strong>
                    </TableCell>
                    <TableCell>
                      {parseFloat(rate.base_weight_kg).toFixed(3)} kg
                    </TableCell>
                    <TableCell>
                      {rate.currency} {parseFloat(rate.extra_per_kg).toFixed(3)}
                    </TableCell>
                    <TableCell>{rate.currency}</TableCell>
                    <TableCell>
                      <StatusBadge active={rate.is_active}>
                        {rate.is_active ? 'Active' : 'Inactive'}
                      </StatusBadge>
                    </TableCell>
                    <TableCell>
                      {rate.created_at
                        ? new Date(rate.created_at).toLocaleDateString()
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <ActionButtonsCell>
                        <Button variant="secondary" style={{ padding: '6px 12px' }} onClick={() => handleEdit(rate)}>
                          <FiEdit />
                        </Button>
                        <Button variant="secondary" style={{ padding: '6px 12px', color: '#dc2626' }} onClick={() => handleDelete(rate.id)}>
                          <FiTrash2 />
                        </Button>
                      </ActionButtonsCell>
                    </TableCell>
                  </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableWrapper>
        )}
      </Container>

      {isModalOpen && (
        <ModalOverlay onClick={handleCloseModal}>
          <ModalContainer onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{editingRate ? 'Edit Shipping Rate' : 'Add New Shipping Rate'}</ModalTitle>
              <CloseButton onClick={handleCloseModal}>
                <FiX />
              </CloseButton>
            </ModalHeader>

            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label>Country *</Label>
                <Select
                  value={formData.country_id}
                  onChange={(e) => setFormData({ ...formData, country_id: e.target.value })}
                  required
                >
                  <option value="">Select a country</option>
                  {countries.map(country => (
                    <option key={country.id} value={country.id}>
                      {country.name} ({country.code})
                    </option>
                  ))}
                </Select>
              </FormGroup>

              <InfoBox>
                <strong>Shipping Calculation:</strong><br/>
                If product weight ≤ base weight: shipping = base price<br/>
                If product weight &gt; base weight: shipping = base price + ceil(extra weight) × extra per kg
              </InfoBox>

              <FormRow>
                <FormGroup>
                  <Label>Base Price *</Label>
                  <Input
                    type="number"
                    step="0.001"
                    min="0"
                    value={formData.base_price}
                    onChange={(e) => setFormData({ ...formData, base_price: e.target.value })}
                    required
                    placeholder="0.000"
                  />
                  <small style={{ color: '#6b7280', fontSize: '12px' }}>
                    Price for base weight
                  </small>
                </FormGroup>
                <FormGroup>
                  <Label>Base Weight (kg) *</Label>
                  <Input
                    type="number"
                    step="0.001"
                    min="0"
                    value={formData.base_weight_kg}
                    onChange={(e) => setFormData({ ...formData, base_weight_kg: e.target.value })}
                    required
                    placeholder="1.000"
                  />
                  <small style={{ color: '#6b7280', fontSize: '12px' }}>
                    Included weight in base price
                  </small>
                </FormGroup>
              </FormRow>

              <FormRow>
                <FormGroup>
                  <Label>Extra Per kg *</Label>
                  <Input
                    type="number"
                    step="0.001"
                    min="0"
                    value={formData.extra_per_kg}
                    onChange={(e) => setFormData({ ...formData, extra_per_kg: e.target.value })}
                    required
                    placeholder="0.000"
                  />
                  <small style={{ color: '#6b7280', fontSize: '12px' }}>
                    Cost per additional kg
                  </small>
                </FormGroup>
                <FormGroup>
                  <Label>Currency *</Label>
                  <Select
                    value={formData.currency}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    required
                  >
                    <option value="OMR">OMR</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                  </Select>
                </FormGroup>
              </FormRow>

              <FormGroup>
                <CheckboxLabel>
                  <Checkbox
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  <span>Active (use this rate for shipping calculations)</span>
                </CheckboxLabel>
              </FormGroup>

              <ButtonGroup>
                <CancelButton type="button" onClick={handleCloseModal}>
                  Cancel
                </CancelButton>
                <Button type="submit" variant="primary" disabled={submitting}>
                  {submitting ? 'Saving...' : editingRate ? 'Update Rate' : 'Add Rate'}
                </Button>
              </ButtonGroup>
            </Form>
          </ModalContainer>
        </ModalOverlay>
      )}
    </>
  );
}





